// services/VendorDataService.js - REAL vendor data management
class VendorDataService {
    constructor() {
      this.VENDOR_DATA_KEY = 'uniledge_vendor_data';
      this.VENDOR_ORDERS_KEY = 'uniledge_vendor_orders';
      this.VENDOR_PRODUCTS_KEY = 'uniledge_vendor_products';
      this.VENDOR_ANALYTICS_KEY = 'uniledge_vendor_analytics';
      this.GROUP_MEMBER_DATA_KEY = 'uniledge_group_members';
    }
  
    // ============ VENDOR DATA MANAGEMENT ============
    
    /**
     * Get or create vendor profile data
     */
    getVendorData(vendorId) {
      const data = localStorage.getItem(`${this.VENDOR_DATA_KEY}_${vendorId}`);
      if (data) {
        return JSON.parse(data);
      }
      
      // Create new vendor profile
      const newVendor = {
        id: vendorId,
        businessName: '',
        email: '',
        phone: '',
        address: {},
        joinedDate: new Date().toISOString(),
        totalRevenue: 0,
        totalOrders: 0,
        totalProducts: 0,
        membersSaved: 0,
        averageRating: 0,
        completionRate: 100,
        lastOrderDate: null
      };
      
      this.saveVendorData(vendorId, newVendor);
      return newVendor;
    }
  
    /**
     * Save vendor profile data
     */
    saveVendorData(vendorId, data) {
      localStorage.setItem(`${this.VENDOR_DATA_KEY}_${vendorId}`, JSON.stringify(data));
    }
  
    // ============ PRODUCT MANAGEMENT ============
    
    /**
     * Get all products for a vendor
     */
    getVendorProducts(vendorId) {
      const products = localStorage.getItem(`${this.VENDOR_PRODUCTS_KEY}_${vendorId}`);
      return products ? JSON.parse(products) : [];
    }
  
    /**
     * Add a new product
     */
    addProduct(vendorId, productData) {
      const products = this.getVendorProducts(vendorId);
      const newProduct = {
        id: `product_${Date.now()}`,
        vendorId,
        name: productData.name,
        description: productData.description || '',
        category: productData.category,
        basePrice: parseFloat(productData.basePrice),
        bulkPricing: productData.bulkPricing || [],
        inventory: productData.inventory || 'In Stock',
        images: productData.images || [],
        addedDate: new Date().toISOString(),
        totalSold: 0,
        totalRevenue: 0,
        currentGroups: 0,
        averageRating: 0,
        active: true
      };
      
      products.push(newProduct);
      localStorage.setItem(`${this.VENDOR_PRODUCTS_KEY}_${vendorId}`, JSON.stringify(products));
      
      // Update vendor stats
      this.updateVendorStats(vendorId);
      
      return newProduct;
    }
  
    /**
     * Update existing product
     */
    updateProduct(vendorId, productId, updates) {
      const products = this.getVendorProducts(vendorId);
      const index = products.findIndex(p => p.id === productId);
      
      if (index === -1) {
        throw new Error('Product not found');
      }
      
      products[index] = { ...products[index], ...updates };
      localStorage.setItem(`${this.VENDOR_PRODUCTS_KEY}_${vendorId}`, JSON.stringify(products));
      
      return products[index];
    }
  
    /**
     * Delete product
     */
    deleteProduct(vendorId, productId) {
      const products = this.getVendorProducts(vendorId);
      const filtered = products.filter(p => p.id !== productId);
      localStorage.setItem(`${this.VENDOR_PRODUCTS_KEY}_${vendorId}`, JSON.stringify(filtered));
      
      // Update vendor stats
      this.updateVendorStats(vendorId);
    }
  
    // ============ ORDER MANAGEMENT ============
    
    /**
     * Get all orders for a vendor
     */
    getVendorOrders(vendorId) {
      const orders = localStorage.getItem(`${this.VENDOR_ORDERS_KEY}_${vendorId}`);
      return orders ? JSON.parse(orders) : [];
    }
  
    /**
     * Create a new order
     */
    createOrder(vendorId, orderData) {
      const orders = this.getVendorOrders(vendorId);
      const newOrder = {
        id: `order_${Date.now()}`,
        vendorId,
        groupId: orderData.groupId,
        groupTitle: orderData.groupTitle,
        productId: orderData.productId,
        productName: orderData.productName,
        quantity: orderData.quantity,
        unitPrice: orderData.unitPrice,
        totalValue: orderData.quantity * orderData.unitPrice,
        memberCount: orderData.memberCount,
        status: 'pending',
        createdAt: new Date().toISOString(),
        deadline: orderData.deadline,
        pickupLocation: orderData.pickupLocation || '',
        memberEmails: orderData.memberEmails || [],
        notes: orderData.notes || ''
      };
      
      orders.push(newOrder);
      localStorage.setItem(`${this.VENDOR_ORDERS_KEY}_${vendorId}`, JSON.stringify(orders));
      
      // Update product stats
      this.updateProductStats(vendorId, orderData.productId, newOrder);
      
      // Update vendor stats
      this.updateVendorStats(vendorId);
      
      return newOrder;
    }
  
    /**
     * Update order status
     */
    updateOrderStatus(vendorId, orderId, status, additionalData = {}) {
      const orders = this.getVendorOrders(vendorId);
      const index = orders.findIndex(o => o.id === orderId);
      
      if (index === -1) {
        throw new Error('Order not found');
      }
      
      const updatedOrder = {
        ...orders[index],
        status,
        ...additionalData,
        updatedAt: new Date().toISOString()
      };
      
      // Add status-specific fields
      if (status === 'confirmed') {
        updatedOrder.confirmedAt = new Date().toISOString();
      } else if (status === 'fulfilled') {
        updatedOrder.fulfilledAt = new Date().toISOString();
      } else if (status === 'completed') {
        updatedOrder.completedAt = new Date().toISOString();
      }
      
      orders[index] = updatedOrder;
      localStorage.setItem(`${this.VENDOR_ORDERS_KEY}_${vendorId}`, JSON.stringify(orders));
      
      // Update vendor stats
      this.updateVendorStats(vendorId);
      
      return updatedOrder;
    }
  
    // ============ ANALYTICS & CALCULATIONS ============
    
    /**
     * Calculate real analytics for vendor dashboard
     */
    calculateVendorAnalytics(vendorId, timeRange = 'week') {
      const orders = this.getVendorOrders(vendorId);
      const products = this.getVendorProducts(vendorId);
      const now = new Date();
      
      // Calculate time boundaries
      let startDate;
      switch (timeRange) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }
      
      // Filter orders by time range
      const periodOrders = orders.filter(order => 
        new Date(order.createdAt) >= startDate
      );
      
      // Calculate current period metrics
      const currentRevenue = periodOrders.reduce((sum, order) => sum + order.totalValue, 0);
      const currentOrderCount = periodOrders.length;
      const activeOrders = orders.filter(o => ['pending', 'confirmed'].includes(o.status)).length;
      const completedOrders = orders.filter(o => o.status === 'completed').length;
      
      // Calculate previous period for growth comparison
      const previousStartDate = new Date(startDate.getTime() - (now.getTime() - startDate.getTime()));
      const previousOrders = orders.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= previousStartDate && orderDate < startDate;
      });
      const previousRevenue = previousOrders.reduce((sum, order) => sum + order.totalValue, 0);
      
      // Calculate growth rate
      const growthRate = previousRevenue > 0 
        ? Math.round(((currentRevenue - previousRevenue) / previousRevenue) * 100)
        : currentRevenue > 0 ? 100 : 0;
      
      // Calculate other metrics
      const totalMembers = this.calculateTotalMembersServed(orders);
      const averageOrderValue = currentOrderCount > 0 ? currentRevenue / currentOrderCount : 0;
      const completionRate = orders.length > 0 ? Math.round((completedOrders / orders.length) * 100) : 100;
      
      return {
        totalSales: currentRevenue,
        weeklyGrowth: growthRate,
        activeOrders,
        pendingOrders: orders.filter(o => o.status === 'pending').length,
        totalProducts: products.filter(p => p.active).length,
        totalMembers,
        averageRating: this.calculateAverageRating(orders),
        completionRate,
        tokensEarned: this.calculateTokensEarned(vendorId),
        jobsPosted: this.getVendorJobsCount(vendorId),
        averageOrderValue: Math.round(averageOrderValue),
        timeRange,
        periodLabel: this.getPeriodLabel(timeRange)
      };
    }
  
    /**
     * Generate sales data for charts (REAL data, not random)
     */
    generateSalesChartData(vendorId, timeRange = 'week') {
      const orders = this.getVendorOrders(vendorId);
      const now = new Date();
      
      let days, startDate;
      switch (timeRange) {
        case 'week':
          days = 7;
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          days = 30;
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          days = 7;
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }
      
      const chartData = [];
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
        
        const dayOrders = orders.filter(order => {
          const orderDate = new Date(order.createdAt);
          return orderDate >= dayStart && orderDate < dayEnd;
        });
        
        const dayRevenue = dayOrders.reduce((sum, order) => sum + order.totalValue, 0);
        
        chartData.push({
          day: timeRange === 'week' ? date.toLocaleDateString('en-US', { weekday: 'short' }) : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          sales: dayRevenue,
          orders: dayOrders.length
        });
      }
      
      return chartData;
    }
  
    /**
     * Get top performing products
     */
    getTopProducts(vendorId, limit = 5) {
      const products = this.getVendorProducts(vendorId);
      return products
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, limit)
        .map(product => ({
          id: product.id,
          name: product.name,
          totalSold: product.totalSold,
          totalRevenue: product.totalRevenue,
          averageRating: product.averageRating
        }));
    }
  
    // ============ HELPER METHODS ============
    
    /**
     * Update vendor aggregate statistics
     */
    updateVendorStats(vendorId) {
      const orders = this.getVendorOrders(vendorId);
      const products = this.getVendorProducts(vendorId);
      const vendor = this.getVendorData(vendorId);
      
      const totalRevenue = orders.reduce((sum, order) => sum + order.totalValue, 0);
      const completedOrders = orders.filter(o => o.status === 'completed');
      const totalMembers = this.calculateTotalMembersServed(orders);
      
      const updatedVendor = {
        ...vendor,
        totalRevenue,
        totalOrders: orders.length,
        totalProducts: products.length,
        membersSaved: totalMembers,
        averageRating: this.calculateAverageRating(orders),
        completionRate: orders.length > 0 ? Math.round((completedOrders.length / orders.length) * 100) : 100,
        lastOrderDate: orders.length > 0 ? orders[orders.length - 1].createdAt : null
      };
      
      this.saveVendorData(vendorId, updatedVendor);
    }
  
    /**
     * Update product-specific statistics
     */
    updateProductStats(vendorId, productId, order) {
      const products = this.getVendorProducts(vendorId);
      const index = products.findIndex(p => p.id === productId);
      
      if (index !== -1) {
        products[index].totalSold += order.quantity;
        products[index].totalRevenue += order.totalValue;
        localStorage.setItem(`${this.VENDOR_PRODUCTS_KEY}_${vendorId}`, JSON.stringify(products));
      }
    }
  
    /**
     * Calculate total unique members served
     */
    calculateTotalMembersServed(orders) {
      const memberEmails = new Set();
      orders.forEach(order => {
        if (order.memberEmails && Array.isArray(order.memberEmails)) {
          order.memberEmails.forEach(email => memberEmails.add(email));
        } else if (order.memberCount) {
          // Estimate based on member count if emails not available
          for (let i = 0; i < order.memberCount; i++) {
            memberEmails.add(`member_${order.id}_${i}`);
          }
        }
      });
      return memberEmails.size;
    }
  
    /**
     * Calculate average rating from completed orders
     */
    calculateAverageRating(orders) {
      const completedOrders = orders.filter(o => o.status === 'completed' && o.rating);
      if (completedOrders.length === 0) return 0;
      
      const totalRating = completedOrders.reduce((sum, order) => sum + (order.rating || 0), 0);
      return Math.round((totalRating / completedOrders.length) * 10) / 10;
    }
  
    /**
     * Calculate tokens earned from logistics jobs
     */
    calculateTokensEarned(vendorId) {
      // This would integrate with the TokenContext/JobsContext
      // For now, return 0 as vendors don't typically earn tokens
      return 0;
    }
  
    /**
     * Get count of logistics jobs posted by vendor
     */
    getVendorJobsCount(vendorId) {
      const jobs = localStorage.getItem('admin_jobs');
      if (!jobs) return 0;
      
      const parsedJobs = JSON.parse(jobs);
      let count = 0;
      
      Object.values(parsedJobs).forEach(groupJobs => {
        count += groupJobs.filter(job => job.postedBy === vendorId || job.vendorId === vendorId).length;
      });
      
      return count;
    }
  
    /**
     * Get human-readable period label
     */
    getPeriodLabel(timeRange) {
      switch (timeRange) {
        case 'week': return 'Last 7 days';
        case 'month': return 'Last 30 days';
        case 'quarter': return 'Last 3 months';
        case 'year': return 'Last 12 months';
        default: return 'Last 7 days';
      }
    }
  
    // ============ GROUP INTEGRATION ============
    
    /**
     * Get available groups for vendor outreach
     */
    getAvailableGroups() {
      // This integrates with the existing groupData.js
      try {
        // Import the generateDemoGroups function if available
        const storedGroups = localStorage.getItem('uniledge_groups');
        if (storedGroups) {
          return JSON.parse(storedGroups);
        }
        
        // Fallback to basic group structure
        return [];
      } catch (error) {
        console.error('Error loading groups:', error);
        return [];
      }
    }
  
    /**
     * Create bulk order proposal for a group
     */
    createBulkOrderProposal(vendorId, groupId, proposalData) {
      const proposals = localStorage.getItem(`bulk_proposals_${vendorId}`) || '[]';
      const parsed = JSON.parse(proposals);
      
      const newProposal = {
        id: `proposal_${Date.now()}`,
        vendorId,
        groupId,
        productId: proposalData.productId,
        tierPricing: proposalData.tierPricing,
        minQuantity: proposalData.minQuantity,
        deadline: proposalData.deadline,
        status: 'pending',
        createdAt: new Date().toISOString(),
        message: proposalData.message || ''
      };
      
      parsed.push(newProposal);
      localStorage.setItem(`bulk_proposals_${vendorId}`, JSON.stringify(parsed));
      
      return newProposal;
    }
  
    // ============ INITIALIZATION ============
    
    /**
     * Initialize vendor data if first time user
     */
    initializeVendor(vendorData) {
      const existingVendor = this.getVendorData(vendorData.id);
      
      if (!existingVendor.businessName && vendorData.businessName) {
        const initializedVendor = {
          ...existingVendor,
          businessName: vendorData.businessName,
          email: vendorData.email,
          phone: vendorData.phone || '',
          address: vendorData.address || {},
          initializedAt: new Date().toISOString()
        };
        
        this.saveVendorData(vendorData.id, initializedVendor);
        return initializedVendor;
      }
      
      return existingVendor;
    }
  }
  
  // Create singleton instance
  const vendorDataService = new VendorDataService();
  
  export default vendorDataService;